import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'
import { fetchUkLevel } from '../components/map/ukAdminApi'
import { AdminLevel, UkAdminCollection } from '../components/map/LocationMapTypes'

export const LEVELS: AdminLevel[] = [1, 2, 3, 4]
export type LevelsData = Record<AdminLevel, UkAdminCollection>

interface DataContextValue {
    data: LevelsData | null
    loading: boolean
    error: Error | null
}

const DataContext = createContext<DataContextValue | undefined>(undefined)
const KEY = "location-levels"

const readFromSession = (): LevelsData | null => {
    try
    {
        const saved = sessionStorage.getItem(KEY)
        if (!saved) return null
        const parsed = JSON.parse(saved) as LevelsData
        return LEVELS.every((l) => parsed[l]?.features) ? parsed : null
    }
    catch
    {
        return null
    }
}

export const LocationDataContext = ({ children }: { children: ReactNode }) => {

    const [data, setData] = useState<LevelsData | null>(readFromSession)
    const [loading, setLoading] = useState<boolean>(() => readFromSession() === null)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        if (data) return

        let cancelled = false;

        (async () => {
            try
            {
                const results = await Promise.all(LEVELS.map((l) => fetchUkLevel(l)))
                if (cancelled) return

                const combined = Object.fromEntries(
                    LEVELS.map((l, i) => [l, results[i]])
                ) as LevelsData

                setData(combined)

                try { sessionStorage.setItem(KEY, JSON.stringify(combined)) }
                catch { /* too large for sessionStorage, keep in memory only */ }
            }
            catch (err)
            {
                if (!cancelled) setError(err instanceof Error ? err : new Error("Unknown error"))
            }
            finally
            {
                if (!cancelled) setLoading(false)
            }
        })()

        return () => { cancelled = true }
    }, [])

    return (
        <DataContext.Provider value={{ data, loading, error }}>
            {children}
        </DataContext.Provider>
    )
}

export const useAdminData = (): DataContextValue => {
    const ctx = useContext(DataContext)
    if (!ctx) throw new Error("useData must be used inside <LocationDataContext>")
    return ctx
}